/**
 * Onboarding React Query Hooks
 * SESSION_199: Onboarding Frontend Step 1 - Business info editing
 * SESSION_200: Onboarding Frontend Step 2 - Contact preferences
 *
 * Provides React Query hooks for user onboarding flow.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { onboardingApi } from '../../infrastructure/api/OnboardingApi';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';
import type {
  OnboardingStatus,
  UpdateBusinessByOwnerRequest,
  CompleteOnboardingResponse,
  UpdateContactPreferencesRequest,
  PendingBusinessClaimInfo
} from '../../domain/entities/Onboarding';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch user's onboarding status query hook.
 * GET /api/users/onboarding-status
 *
 * Returns whether onboarding is complete and associated business info.
 *
 * @param enabled - Whether the query should run (default: true)
 * @returns The user's onboarding status and business info
 */
export const useOnboardingStatusQuery = (
  enabled: boolean = true
): UseQueryResult<OnboardingStatus, Error> => {
  return useQuery({
    queryKey: queryKeys.onboarding.status(),
    queryFn: async (): Promise<OnboardingStatus> => {
      return onboardingApi.getOnboardingStatus();
    },
    staleTime: CACHE_CONFIG.USER.staleTime,
    gcTime: CACHE_CONFIG.USER.gcTime,
    enabled
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Complete onboarding mutation hook.
 * POST /api/users/complete-onboarding
 *
 * Marks the user's onboarding as complete.
 */
export const useCompleteOnboardingMutation = (): UseMutationResult<
  CompleteOnboardingResponse,
  Error,
  void,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<CompleteOnboardingResponse> => {
      return onboardingApi.completeOnboarding();
    },
    onSuccess: (data): void => {
      // Invalidate onboarding status to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.all });
      // Also invalidate user queries since onboarding status is part of user
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });

      if (data.success) {
        notificationService.showSuccess(
          'Onboarding completed successfully!',
          'Welcome'
        );
      }
    },
    onError: (error: Error): void => {
      console.error('Complete onboarding failed:', error);
      notificationService.showError(
        'Failed to complete onboarding. Please try again.',
        'Error'
      );
    }
  });
};

/**
 * Update business by owner mutation params.
 */
interface UpdateBusinessByOwnerParams {
  businessId: string;
  request: UpdateBusinessByOwnerRequest;
}

/**
 * Update business by owner mutation hook.
 * PUT /api/businesses/{id}/owner-update
 *
 * Allows business owners to update: phone, email, website, description.
 * Name and address are NOT editable (use address correction flow).
 */
export const useUpdateBusinessByOwnerMutation = (): UseMutationResult<
  void,
  Error,
  UpdateBusinessByOwnerParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateBusinessByOwnerParams): Promise<void> => {
      return onboardingApi.updateBusinessByOwner(params.businessId, params.request);
    },
    onSuccess: (): void => {
      // Invalidate onboarding status to refetch fresh data with updated business info
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.all });
      // Also invalidate business queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });

      notificationService.showSuccess(
        'Business information updated successfully.',
        'Saved'
      );
    },
    onError: (error: Error): void => {
      console.error('Update business failed:', error);
      notificationService.showError(
        'Failed to update business information. Please try again.',
        'Error'
      );
    }
  });
};

/**
 * Update contact preferences mutation hook.
 * PUT /api/users/contact-preferences
 * SESSION_200: Onboarding Step 2 - Contact Preferences
 *
 * Updates user's email/SMS notification preferences and phone number.
 */
export const useUpdateContactPreferencesMutation = (): UseMutationResult<
  void,
  Error,
  UpdateContactPreferencesRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateContactPreferencesRequest): Promise<void> => {
      return onboardingApi.updateContactPreferences(request);
    },
    onSuccess: (): void => {
      // Invalidate onboarding status to refetch fresh data with updated preferences
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.all });
      // Also invalidate user queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });

      notificationService.showSuccess(
        'Contact preferences updated successfully.',
        'Saved'
      );
    },
    onError: (error: Error): void => {
      console.error('Update contact preferences failed:', error);
      notificationService.showError(
        'Failed to update contact preferences. Please try again.',
        'Error'
      );
    }
  });
};

// ============================================================================
// Helper Hooks
// ============================================================================

/**
 * Hook to check if user has completed onboarding.
 * Convenience wrapper around useOnboardingStatusQuery.
 *
 * SESSION_338: Extended to return pending business claim info for route access control.
 * Users with pending new business claims should be allowed portal access.
 *
 * @param enabled - Whether to enable the query
 * @returns Object with hasCompletedOnboarding boolean, pending claim info, and loading state
 */
export const useHasCompletedOnboarding = (
  enabled: boolean = true
): {
  hasCompletedOnboarding: boolean;
  hasPendingBusinessClaim: boolean;
  pendingBusinessClaim: PendingBusinessClaimInfo | null;
  isLoading: boolean;
} => {
  const { data, isLoading } = useOnboardingStatusQuery(enabled);

  return {
    hasCompletedOnboarding: data?.hasCompletedOnboarding ?? false,
    hasPendingBusinessClaim: data?.pendingBusinessClaim != null,
    pendingBusinessClaim: data?.pendingBusinessClaim ?? null,
    isLoading
  };
};

// ============================================================================
// Claim Business Details
// SESSION_339: Enable full onboarding for pending claims
// ============================================================================

/**
 * Update claim business details mutation params.
 */
interface UpdateClaimBusinessDetailsParams {
  claimId: string;
  request: UpdateBusinessByOwnerRequest;
}

/**
 * Update business details on a pending business claim.
 * PUT /api/business-claims/{claimId}/business-details
 * SESSION_339: Enable full onboarding for pending claims
 *
 * Allows users to update phone, email, website, description during onboarding
 * for new business claims that are pending admin approval.
 */
export const useUpdateClaimBusinessDetailsMutation = (): UseMutationResult<
  void,
  Error,
  UpdateClaimBusinessDetailsParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateClaimBusinessDetailsParams): Promise<void> => {
      return onboardingApi.updateClaimBusinessDetails(params.claimId, params.request);
    },
    onSuccess: (): void => {
      // Invalidate onboarding status to refetch fresh data with updated business details
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.all });

      notificationService.showSuccess(
        'Business information updated successfully.',
        'Saved'
      );
    },
    onError: (error: Error): void => {
      console.error('Update claim business details failed:', error);
      notificationService.showError(
        'Failed to update business information. Please try again.',
        'Error'
      );
    }
  });
};

// ============================================================================
// Address Correction
// SESSION_201: Address correction feature
// ============================================================================

/**
 * Submit address correction mutation hook.
 * POST /api/address-corrections
 *
 * Allows users to request corrections to their business address.
 */
export const useSubmitAddressCorrectionMutation = (): UseMutationResult<
  { success: boolean; requestId?: string; message?: string },
  Error,
  { businessId: string; requestedAddress: string },
  unknown
> => {
  return useMutation({
    mutationFn: async (request: { businessId: string; requestedAddress: string }) => {
      return onboardingApi.submitAddressCorrection(request);
    },
    onSuccess: (data): void => {
      if (data.success) {
        notificationService.showSuccess(
          'Your address correction request has been submitted. We\'ll review it shortly.',
          'Request Submitted'
        );
      }
    },
    onError: (error: Error): void => {
      console.error('Submit address correction failed:', error);
      notificationService.showError(
        'Failed to submit address correction. Please try again.',
        'Error'
      );
    }
  });
};
